import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Table,
  Modal,
  Form,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Alert,
  Accordion
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layouts/Main';
import config from '../config';
import { toast } from 'react-toastify';
import {
  FiRefreshCw,
  FiPlus,
  FiShoppingCart,
  FiPackage,
  FiTruck,
  FiEye,
  FiArrowRight,
  FiSettings,
  FiAlertCircle
} from 'react-icons/fi';
import {
  MdInventory,
  MdPrecisionManufacturing,
  MdOutlineWarehouse
} from 'react-icons/md';
import { BiCartAdd } from 'react-icons/bi';
import './OperationsDashboard.css';

import SalesOrdersTab from '../components/operationalDashboard/SalesOrdersTab';
import DashboardModals from '../components/operationalDashboard/DashboardModals';
import AllocationSuggestionModal from '../components/operationalDashboard/AllocationSuggestionModal';

import PurchaseRequestModal from '../components/modals/PurchaseRequestModal';
// import { loggedInUserId } from '../utils/loggedInUserDetails';
import { useDashboardActions } from '../hooks/useDashboardActions';

const OperationsDashboard = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Statistics
  const [stats, setStats] = useState({
    totalSO: 0,
    newSO: 0,
    inProduction: 0,
    totalPO: 0,
    pendingDelivery: 0,
    lowStockItems: 0,
    reservedStock: 0
  });

  const [todayStats, setTodayStats] = useState({
    salesOrders: 0,
    purchaseOrders: 0,
    productionRequests: 0,
    completedBatches: 0
  });

  const [pendingSuggestions, setPendingSuggestions] = useState([]);

  // Modal states
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState(null);

  // Load pending allocation suggestions
  const loadPendingSuggestions = useCallback(async () => {
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/allocation/suggestions/pending`
      );
      const suggestions = response.data.data || [];
      setPendingSuggestions(suggestions);

      // Auto-show modal if there are new suggestions and modal isn't already open
      if (suggestions.length > 0 && !showAllocationModal && !currentSuggestion) {
        // Check if this suggestion was dismissed today
        const dismissedToday = localStorage.getItem('dismissedAllocationSuggestions');
        const dismissedDate = dismissedToday ? new Date(dismissedToday).toDateString() : null;
        const today = new Date().toDateString();

        // Only auto-show if not dismissed today
        if (dismissedDate !== today) {
          setCurrentSuggestion(suggestions[0]);
          // setShowAllocationModal(true);
        }
      }
    } catch (error) {
      console.error('Error loading allocation suggestions:', error);
      // Don't show error toast as this runs in background
    }
  }, [showAllocationModal, currentSuggestion]);

  // Data states
  const [salesOrders, setSalesOrders] = useState({
    draft: [],
    new: [],
    inProduction: [],
    dispatched: []
  });
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [reservedStock, setReservedStock] = useState({});
  const [stockSummary, setStockSummary] = useState([]);
  const [pmStockSummary, setPmStockSummary] = useState([]);

  const [showStockDetailModal, setShowStockDetailModal] = useState(false);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [showReservedStockModal, setShowReservedStockModal] = useState(false);
  const [showPurchaseRequestCreateModal, setShowPurchaseRequestCreateModal] = useState(false);
  const [showPendingPOModal, setShowPendingPOModal] = useState(false);
  const [showRunningBatchesModal, setShowRunningBatchesModal] = useState(false);

  // Selected data states
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedItems] = useState([]);
  const [reservedStockOrders, setReservedStockOrders] = useState([]);
  const [loadingReservedStock, setLoadingReservedStock] = useState(false);
  const [selectedReservedProduct, setSelectedReservedProduct] = useState(null);

  const [pendingPOs, setPendingPOs] = useState([]);
  const [runningBatches, setRunningBatches] = useState([]);
  const [selectedProductPOs, setSelectedProductPOs] = useState([]);
  const [selectedProductBatches, setSelectedProductBatches] = useState([]);
  const [loadingPendingPOs, setLoadingPendingPOs] = useState(false);
  const [loadingRunningBatches, setLoadingRunningBatches] = useState(false);

  // Filter states
  const [poStatusFilter, setPoStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [soTabFilter, setSoTabFilter] = useState('new'); // Default to 'new' which shows only Created status

  // Allocation suggestion states
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      const [
        soResponse,
        poResponse,
        stockResponse,
        pmStockResponse,
        todayStatsResponse,
        pendingPOResponse,
        runningBatchesResponse
      ] = await Promise.allSettled([
        axios.get(`${config.API_BASE_URL}/sales-orders?populateItems=true&populateBatch=true`),
        axios.get(`${config.API_BASE_URL}/purchase-orders?populate=true&populateItems=true`),
        axios.get(`${config.API_BASE_URL}/inventory/stock-overview`),
        axios.get(`${config.API_BASE_URL}/pm-inventory/stock-overview`),
        axios.get(`${config.API_BASE_URL}/dashboard/today-stats`),
        axios.get(`${config.API_BASE_URL}/purchase-orders/pending-by-product`),
        axios.get(`${config.API_BASE_URL}/production-batches/running-by-product`)
      ]);

      if (soResponse.status === 'fulfilled') {
        const allOrders = soResponse.value.data;
        const categorized = {
          draft: allOrders.filter(o => o.status === 'Draft'),
          new: allOrders.filter(o => ['Created', 'Confirmed', 'Stock Pending', 'PO Received'].includes(o.status)),
          inProduction: allOrders.filter(o => ['In Production', 'Production Requested', 'Production Complete'].includes(o.status) || o.productionBatch),
          dispatched: allOrders.filter(o => ['Partially Dispatched', 'Fully Dispatched', 'In Transit', 'Partially Delivered', 'Delivered', 'Completed'].includes(o.status))
        };
        // setSalesOrders(categorized);
        setSalesOrders(allOrders);
        calculateStats(
          // categorized,
          allOrders,
          poResponse.status === 'fulfilled' ? poResponse.value.data : [],
          stockResponse.status === 'fulfilled' ? stockResponse.value.data : {}
        );
      } else {
        setSalesOrders({ draft: [], new: [], inProduction: [], dispatched: [] });
      }

      setPurchaseOrders(poResponse.status === 'fulfilled' ? poResponse.value.data : []);
      setStockSummary(stockResponse.status === 'fulfilled' ? stockResponse.value.data.products || [] : []);
      setPmStockSummary(pmStockResponse.status === 'fulfilled' ? pmStockResponse.value.data.products || [] : []);
      setReservedStock(stockResponse.status === 'fulfilled' ? stockResponse.value.data.reservedStock || {} : {});
      setTodayStats(todayStatsResponse.status === 'fulfilled' ? todayStatsResponse.value.data : {});
      setPendingPOs(pendingPOResponse.status === 'fulfilled' ? pendingPOResponse.value.data || [] : []);
      setRunningBatches(runningBatchesResponse.status === 'fulfilled' ? runningBatchesResponse.value.data || [] : []);

      calculateStats(
        soResponse.status === 'fulfilled' ? soResponse.value.data : {},
        poResponse.status === 'fulfilled' ? poResponse.value.data : [],
        stockResponse.status === 'fulfilled' ? stockResponse.value.data : {}
      );

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  const {
    showBOMModal, setShowBOMModal,
    showProductionBatchModal, setShowProductionBatchModal,
    selectedSO, setSelectedSO,
    selectedItem, setSelectedItem,
    bomList,
    productionBatches,
    loadingBOM,
    loadingBatches,
    getSOStatusBadge,
    getPOStatusBadge,
    handleRaiseProductionRequest,
    handleCheckBOM,
    handleRunningProduction,
    handleCreatePurchaseRequest,
    handleCreateCommonPurchaseRequest,
    handleCreateFGPR,
    handleAssignToBatch
  } = useDashboardActions(fetchDashboardData);

  useEffect(() => {
    fetchDashboardData();
    loadPendingSuggestions();

    // Poll for suggestions every 30 seconds
    const interval = setInterval(loadPendingSuggestions, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboardData, loadPendingSuggestions]);

  const calculateStats = (soData, poData, stockResponse) => {
    const stockData = stockResponse.products || [];
    const reservedStockData = stockResponse.reservedStock || {};

    const totalSO = (soData.draft?.length || 0) + (soData.new?.length || 0) + (soData.inProduction?.length || 0) + (soData.dispatched?.length || 0);

    const stats = {
      totalSO,
      newSO: soData.new?.length || 0,
      inProduction: soData.inProduction?.length || 0,
      totalPO: poData.length,
      pendingDelivery: poData.filter(po => ['Created', 'Partial Received'].includes(po.status)).length,
      lowStockItems: stockData.filter(item => {
        const availableStock = (item.totalAvailableStock || 0) - (reservedStockData[item.product._id] || 0);
        return availableStock <= (item.product.bufferStock || 0);
      }).length,
      reservedStock: Object.values(reservedStockData).reduce((sum, reserved) => sum + reserved, 0)
    };
    setStats(stats);
  };

  const getStockStatusBadge = (product) => {
    const availableStock = (product.totalAvailableStock || 0) - (reservedStock[product.product._id] || 0);
    const bufferStock = product.product.bufferStock || 0;

    if (availableStock <= 0) {
      return <Badge bg="danger">Out of Stock</Badge>;
    } else if (availableStock <= bufferStock) {
      return <Badge bg="warning">Low Stock</Badge>;
    } else {
      return <Badge bg="success">In Stock</Badge>;
    }
  };

  const getPMStockStatusBadge = (pm) => {
    const availableStock = pm.totalAvailableStock || 0;
    const reorderLevel = pm.packingMaterial.reorderQuantity || 0;

    if (availableStock <= 0) {
      return <Badge bg="danger">Out of Stock</Badge>;
    } else if (availableStock <= reorderLevel) {
      return <Badge bg="warning">Low Stock</Badge>;
    } else {
      return <Badge bg="success">In Stock</Badge>;
    }
  };

  const handleCreateProduction = (so) => {
    // Client-side navigation to production request creation page with pre-filled data (no reload)
    const salesOrderData = encodeURIComponent(JSON.stringify({
      salesOrderId: so._id,
      orderNumber: so.orderNumber,
      items: so.items
    }));
    navigate(`/production-requests/create?salesOrder=${salesOrderData}`);
  };

  const handleDispatchSO = (so) => {
    // Navigate to sales order details page with dispatch tab active
    navigate(`/sales-orders/${so._id}?tab=dispatch`);
  };

  const handleViewStock = (product) => {
    setSelectedProduct(product);
    setShowStockDetailModal(true);
  };

  const handleViewReservedStock = async (item) => {
    setLoadingReservedStock(true);
    setShowReservedStockModal(true);
    // remember product for modal header
    setSelectedReservedProduct(item.product || null);

    try {
      const response = await axios.get(`${config.API_BASE_URL}/sales-orders/reserved-stock/${item.product._id}`);
      setReservedStockOrders(response.data);
    } catch (error) {
      console.error('Error fetching reserved stock details:', error);
      toast.error('Error loading reserved stock details');
    } finally {
      setLoadingReservedStock(false);
    }
  };

  const handleViewPendingPOs = async (item) => {
    setLoadingPendingPOs(true);
    setShowPendingPOModal(true);
    setSelectedProduct(item.product);

    try {
      const response = await axios.get(`${config.API_BASE_URL}/purchase-orders/pending-by-product/${item.product._id}`);
      setSelectedProductPOs(response.data);
    } catch (error) {
      console.error('Error fetching pending POs:', error);
      toast.error('Error loading pending purchase orders');
    } finally {
      setLoadingPendingPOs(false);
    }
  };

  const handleViewRunningBatches = async (item) => {
    setLoadingRunningBatches(true);
    setShowRunningBatchesModal(true);
    setSelectedProduct(item.product);

    try {
      const response = await axios.get(`${config.API_BASE_URL}/production-batches/running-by-product/${item.product._id}`);
      setSelectedProductBatches(response.data);
    } catch (error) {
      console.error('Error fetching running batches:', error);
      toast.error('Error loading running production batches');
    } finally {
      setLoadingRunningBatches(false);
    }
  };

  // Helper functions to calculate pending quantities
  const getPendingPOQuantity = (productId) => {
    const productPOs = pendingPOs.filter(po => po.product === productId);
    return productPOs.reduce((total, po) => total + (po.pendingQuantity || 0), 0);
  };

  const getRunningBatchQuantity = (productId) => {
    const productBatches = runningBatches.filter(batch => batch.product === productId);
    return productBatches.reduce((total, batch) => total + (batch.quantity || 0), 0);
  };

  const handleGeneratePurchaseRequest = async (product) => {
    setSelectedProduct(product);
    setShowPurchaseRequestCreateModal(true);
  };

  const getActionButtons = (so) => {
    const buttons = [];

    if (so.status === 'Created' || so.status === 'Confirmed' || so.status === 'Finalized') {
      // Check if items have sufficient stock
      const hasStockIssues = so.items?.some(item => {
        const productStock = stockSummary.find(s => s.product._id === item.product._id);
        const availableStock = (productStock?.totalAvailableStock || 0) - (reservedStock[item.product._id] || 0);
        return availableStock < item.quantity;
      });

      if (hasStockIssues) {
        buttons.push(
          <Button
            key="production"
            variant="warning"
            size="sm"
            className="me-1"
            onClick={() => handleCreateProduction(so)}
          >
            <FiSettings className="me-1" />Production
          </Button>
        );
      } else {
        buttons.push(
          <Button
            key="dispatch"
            variant="success"
            size="sm"
            className="me-1"
            onClick={() => handleDispatchSO(so)}
          >
            <FiTruck className="me-1" />Dispatch
          </Button>
        );
      }
    }

    buttons.push(
      <Link
        key="view"
        to={`/sales-orders/${so._id}`}
        className="btn btn-outline-primary btn-sm"
      >
        <FiEye className="me-1" />View
      </Link>
    );

    return <ButtonGroup>{buttons}</ButtonGroup>;
  };

  const filteredPurchaseOrders = purchaseOrders.filter(po => {
    if (poStatusFilter === 'all') return true;
    if (poStatusFilter === 'pending') return ['Created'].includes(po.status);
    if (poStatusFilter === 'receiving') return ['Partial Received'].includes(po.status);
    if (poStatusFilter === 'received') return ['Fully Received'].includes(po.status);
    return po.status === poStatusFilter;
  });

  const filteredStock = stockSummary.filter(item => {
    const matchesProduct = productFilter === '' ||
      item.product.productName.toLowerCase().includes(productFilter.toLowerCase());

    let matchesStatus = true;
    if (stockFilter === 'low') {
      const availableStock = (item.totalAvailableStock || 0) - (reservedStock[item.product._id] || 0);
      matchesStatus = availableStock <= (item.product.bufferStock || 0);
    } else if (stockFilter === 'reserved') {
      matchesStatus = (reservedStock[item.product._id] || 0) > 0;
    } else if (stockFilter === 'available') {
      const availableStock = (item.totalAvailableStock || 0) - (reservedStock[item.product._id] || 0);
      matchesStatus = availableStock > (item.product.bufferStock || 0);
    }

    return matchesProduct && matchesStatus;
  });

  if (loading) {
    return (
      <Layout title="Operational Dashboard">
        <Container fluid className="py-4">
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading Operations Dashboard...</p>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Operational Dashboard">
      <Container fluid className="py-4 operations-dashboard">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">Operations Dashboard</h2>
                <p className="text-muted mb-0">
                  Complete overview of Sales Orders, Purchase Orders, Stock & Production
                </p>
                <small className="text-info d-none">
                  Today's Activity: {todayStats.salesOrders} SO, {todayStats.purchaseOrders} PO, {todayStats.productionRequests} PR, {todayStats.completedBatches} Completed Batches
                </small>
              </div>
              <div className="d-flex gap-2">
                {pendingSuggestions.length > 0 && (
                  <Button
                    variant="warning"
                    onClick={() => {
                      setCurrentSuggestion(pendingSuggestions[0]);
                      setShowAllocationModal(true);
                    }}
                  >
                    <FiAlertCircle className="me-1" />
                    {/* {pendingSuggestions.length}  */}
                    Stock Allocation{pendingSuggestions.length > 1 ? 's' : ''} Pending
                  </Button>
                )}
                <Button
                  variant="outline-primary"
                  onClick={fetchDashboardData}
                  disabled={loading}
                >
                  <FiRefreshCw className="me-1" />Refresh
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/purchase_requests')}
                >
                  <FiPlus className="me-1" />Quick Purchase Request
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {/* Stock Alerts */}
        {!loading && (() => {
          const alerts = [];
          const lowStockProducts = stockSummary.filter(item => {
            const availableStock = (item.totalAvailableStock || 0) - (reservedStock[item.product._id] || 0);
            return availableStock <= (item.product.bufferStock || 0);
          });

          const lowStockPM = pmStockSummary.filter(item => {
            const availableStock = item.totalAvailableStock || 0;
            const reorderLevel = item.packingMaterial?.reorderQuantity || 0;
            return availableStock <= reorderLevel;
          });

          if (lowStockProducts.length > 0) {
            alerts.push(
              <Alert variant="warning" className="mb-3" key="product-alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Low Stock Alert:</strong> {lowStockProducts.length} product(s) have low stock levels.
                <Link to="/dashboard?tab=stock" className="alert-link ms-2">View Inventory</Link>
              </Alert>
            );
          }

          if (lowStockPM.length > 0) {
            alerts.push(
              <Alert variant="warning" className="mb-3" key="pm-alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                <strong>Packing Material Alert:</strong> {lowStockPM.length} packing material(s) need attention.
                <Link to="/dashboard?tab=pmstock" className="alert-link ms-2">View Inventory</Link>
              </Alert>
            );
          }

          return alerts;
        })()}

        {/* Today's Statistics Cards */}
        <Row className="mb-4 d-none">
          <Col md={6} xl={3} className="mb-3">
            <Card className="stat-card border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="stat-icon bg-primary text-white rounded-circle mx-auto mb-2">
                  <FiShoppingCart />
                </div>
                <h4 className="text-primary mb-1">{todayStats.salesOrders}</h4>
                <p className="text-muted mb-0">Today's Sales Orders</p>
                <small className="text-success">
                  Total: {stats.totalSO}
                </small>
                <div className="mt-2">
                  <Link to="/sales-orders" className="btn btn-outline-primary btn-sm">
                    View All
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} xl={3} className="mb-3">
            <Card className="stat-card border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="stat-icon bg-success text-white rounded-circle mx-auto mb-2">
                  <FiPackage />
                </div>
                <h4 className="text-success mb-1">{todayStats.purchaseOrders}</h4>
                <p className="text-muted mb-0">Today's Purchase Orders</p>
                <small className="text-warning">
                  Total: {stats.totalPO}
                </small>
                <div className="mt-2">
                  <Link to="/purchase-orders" className="btn btn-outline-success btn-sm">
                    View All
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} xl={3} className="mb-3">
            <Card className="stat-card border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="stat-icon bg-warning text-white rounded-circle mx-auto mb-2">
                  <MdInventory />
                </div>
                <h4 className="text-warning mb-1">{stats.lowStockItems}</h4>
                <p className="text-muted mb-0">Low Stock Items</p>
                <small className="text-info">
                  {stats.reservedStock} units reserved
                </small>
                <div className="mt-2">
                  <Link to="/inventory" className="btn btn-outline-warning btn-sm">
                    View Stock
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6} xl={3} className="mb-3">
            <Card className="stat-card border-0 shadow-sm">
              <Card.Body className="text-center">
                <div className="stat-icon bg-info text-white rounded-circle mx-auto mb-2">
                  <MdPrecisionManufacturing />
                </div>
                <h4 className="text-info mb-1">{todayStats.productionRequests}</h4>
                <p className="text-muted mb-0">Today's Production Requests</p>
                <small className="text-primary">
                  {stats.inProduction} in production
                </small>
                <div className="mt-2">
                  <Link to="/production-requests" className="btn btn-outline-info btn-sm">
                    View All
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Main Content Buttons (replaces Tabs with button view) */}
        <div className="mb-4">
          <div className="btn-group dashboard-tab-buttons w-100" role="tablist" aria-label="Main dashboard views">
            <button
              type="button"
              className={`btn btn-${activeTab === 'overview' ? 'primary' : 'outline-primary'}`}
              onClick={() => setActiveTab('overview')}
            >
              Sales Orders Management
            </button>
            <button
              type="button"
              className={`btn btn-${activeTab === 'stock' ? 'primary' : 'outline-primary'}`}
              onClick={() => setActiveTab('stock')}
            >
              Stock Overview
            </button>
            <button
              type="button"
              className={`btn btn-${activeTab === 'pmstock' ? 'primary' : 'outline-primary'}`}
              onClick={() => setActiveTab('pmstock')}
            >
              PM Stock Overview
            </button>
            <button
              type="button"
              className={`btn btn-${activeTab === 'purchase' ? 'primary' : 'outline-primary'}`}
              onClick={() => setActiveTab('purchase')}
            >
              Recent Purchase Orders
            </button>
          </div>
        </div>

        {/* Panels - render based on activeTab */}
        <div>
          {activeTab === 'overview' && (
            <SalesOrdersTab
              salesOrders={salesOrders}
              soTabFilter={soTabFilter}
              setSoTabFilter={setSoTabFilter}
              stockSummary={stockSummary}
              reservedStock={reservedStock}
              getSOStatusBadge={getSOStatusBadge}
              getActionButtons={getActionButtons}
              handleCheckBOM={handleCheckBOM}
              handleRunningProduction={handleRunningProduction}
              fetchDashboardData={fetchDashboardData}
            />
          )}

          {activeTab === 'stock' && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Search products..."
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Select
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                  >
                    <option value="all">All Stock</option>
                    <option value="low">Low Stock Only</option>
                    <option value="reserved">Reserved Stock</option>
                    <option value="available">Available Stock</option>
                  </Form.Select>
                </Col>
              </Row>

              {/* Group products by category */}
              {(() => {
                const groupedStock = filteredStock.reduce((acc, item) => {
                  const groupId = item.product.groupId?._id || 'ungrouped';
                  const groupName = item.product.groupId?.groupName || 'Ungrouped';

                  if (!acc[groupId]) {
                    acc[groupId] = {
                      _id: groupId,
                      groupName: groupName,
                      products: []
                    };
                  }
                  acc[groupId].products.push(item);
                  return acc;
                }, {});

                // Calculate group summaries
                const getGroupSummary = (products) => {
                  let totalCurrent = 0;
                  let totalReserved = 0;
                  let totalAvailable = 0;
                  let totalPending = 0;
                  let totalInProduction = 0;

                  products.forEach(item => {
                    const availableStock = (item.totalAvailableStock || 0) - (reservedStock[item.product._id] || 0);
                    const pendingQty = getPendingPOQuantity(item.product._id);
                    const batchQty = getRunningBatchQuantity(item.product._id);

                    totalCurrent += item.totalAvailableStock || 0;
                    totalReserved += reservedStock[item.product._id] || 0;
                    totalAvailable += availableStock;
                    totalPending += pendingQty;
                    totalInProduction += batchQty;
                  });

                  return {
                    totalCurrent,
                    totalReserved,
                    totalAvailable,
                    totalPending,
                    totalInProduction
                  };
                };

                return (
                  <Accordion defaultActiveKey="0">
                    {Object.values(groupedStock).map((group, index) => {
                      const summary = getGroupSummary(group.products);
                      const unit = group.products[0]?.product.unitId?.unitCode || '';

                      return (
                        <Accordion.Item eventKey={index.toString()} key={group._id} className="mb-3">
                          <Accordion.Header className="bg-light">
                            <div className="d-flex flex-column flex-md-row justify-content-between w-100 align-items-start align-items-md-center gap-2">
                              <div className="d-flex align-items-center">
                                <strong className="text-primary me-3">{group.groupName}</strong>
                                <Badge bg="secondary" className="fs-11">
                                  {group.products.length} products
                                </Badge>
                              </div>
                              <div className="d-flex flex-wrap gap-2 align-items-center">
                                <div className="d-flex align-items-center gap-1">
                                  <Badge bg="primary" className="d-flex align-items-center gap-1 fs-11" title="Total current stock in this group">
                                    <MdInventory size={12} />
                                    <span>{summary.totalCurrent} {unit}</span>
                                  </Badge>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                  <Badge bg="warning" className="d-flex align-items-center gap-1 fs-11" title="Stock reserved for sales orders in this group">
                                    <FiPackage size={12} />
                                    <span>{summary.totalReserved} {unit}</span>
                                  </Badge>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                  <Badge bg="info" className="d-flex align-items-center gap-1 fs-11" title="Pending purchase order quantities in this group">
                                    <FiShoppingCart size={12} />
                                    <span>{summary.totalPending} {unit}</span>
                                  </Badge>
                                </div>
                                <div className="d-flex align-items-center gap-1">
                                  <Badge bg="success" className="d-flex align-items-center gap-1 fs-11" title="Quantities currently in production in this group">
                                    <MdPrecisionManufacturing size={12} />
                                    <span>{summary.totalInProduction} {unit}</span>
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body className="p-0">
                            <div className="table-responsive border-top">
                              <Table hover className="mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th className="border-0 fw-semibold">Product</th>
                                    <th className="border-0 fw-semibold text-center">Current Stock</th>
                                    <th className="border-0 fw-semibold text-center">Reserved</th>
                                    <th className="border-0 fw-semibold text-center">Available</th>
                                    <th className="border-0 fw-semibold text-center">PO Pending</th>
                                    <th className="border-0 fw-semibold text-center">In Production</th>
                                    <th className="border-0 fw-semibold text-center">Min Stock</th>
                                    <th className="border-0 fw-semibold text-center">Status</th>
                                    <th className="border-0 fw-semibold text-center">Warehouses</th>
                                    <th className="border-0 fw-semibold text-center">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.products.map((item) => {
                                    const availableStock = (item.totalAvailableStock || 0) - (reservedStock[item.product._id] || 0);
                                    const unit = item.product.unitId?.unitCode || '';

                                    return (
                                      <tr key={item.product._id} className="align-middle">
                                        <td className="border-0">
                                          <div>
                                            <strong className="text-dark">{item.product.productName}</strong>
                                            <br />
                                            <small className="text-muted">{item.product.productCode}</small>
                                          </div>
                                        </td>
                                        <td className="border-0 text-center">
                                          <strong className="text-primary">
                                            {item.totalAvailableStock} {unit}
                                          </strong>
                                        </td>
                                        <td className="border-0 text-center">
                                          <span className="text-warning fw-semibold">
                                            {reservedStock[item.product._id] || 0} {unit}
                                          </span>
                                          {(reservedStock[item.product._id] || 0) > 0 && (
                                            <div className="mt-1">
                                              <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 text-decoration-none fw-normal"
                                                onClick={() => handleViewReservedStock(item)}
                                              >
                                                View SO
                                              </Button>
                                            </div>
                                          )}
                                        </td>
                                        <td className="border-0 text-center">
                                          <strong className={`fw-semibold ${availableStock <= 0 ? 'text-danger' : availableStock <= (item.product.bufferStock || 0) ? 'text-warning' : 'text-success'}`}>
                                            {availableStock} {unit}
                                          </strong>
                                        </td>
                                        <td className="border-0 text-center">
                                          {(() => {
                                            const pendingQty = getPendingPOQuantity(item.product._id);
                                            return (
                                              <>
                                                <span className="text-info fw-semibold">
                                                  {pendingQty} {unit}
                                                </span>
                                                {pendingQty > 0 && (
                                                  <div className="mt-1">
                                                    <Button
                                                      variant="link"
                                                      size="sm"
                                                      className="p-0 text-decoration-none fw-normal"
                                                      onClick={() => handleViewPendingPOs(item)}
                                                    >
                                                      View PO
                                                    </Button>
                                                  </div>
                                                )}
                                              </>
                                            );
                                          })()}
                                        </td>
                                        <td className="border-0 text-center">
                                          {(() => {
                                            const batchQty = getRunningBatchQuantity(item.product._id);
                                            return (
                                              <>
                                                <span className="text-success fw-semibold">
                                                  {batchQty} {unit}
                                                </span>
                                                {batchQty > 0 && (
                                                  <div className="mt-1">
                                                    <Button
                                                      variant="link"
                                                      size="sm"
                                                      className="p-0 text-decoration-none fw-normal"
                                                      onClick={() => handleViewRunningBatches(item)}
                                                    >
                                                      View Batches
                                                    </Button>
                                                  </div>
                                                )}
                                              </>
                                            );
                                          })()}
                                        </td>
                                        <td className="border-0 text-center">
                                          <span className="text-muted fw-semibold">{item.product.bufferStock || 0} {unit}</span>
                                        </td>
                                        <td className="border-0 text-center">
                                          {getStockStatusBadge(item)}
                                        </td>
                                        <td className="border-0 text-center">
                                          <Button
                                            variant="outline-info"
                                            size="sm"
                                            onClick={() => handleViewStock(item)}
                                          >
                                            <MdOutlineWarehouse className="me-1" />
                                            {item.stockByLocation?.length || 0}
                                          </Button>
                                        </td>
                                        <td className="border-0 text-center">
                                          <ButtonGroup size="sm">
                                            <OverlayTrigger
                                              placement="top"
                                              overlay={<Tooltip>View Product Details</Tooltip>}
                                            >
                                              <Link
                                                to={`/inventory/${item.product._id}`}
                                                className="btn btn-outline-primary btn-sm"
                                              >
                                                <FiEye />
                                              </Link>
                                            </OverlayTrigger>

                                            {availableStock <= (item.product.bufferStock || 0) && (
                                              <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>Auto-Generate Purchase Request</Tooltip>}
                                              >
                                                <Button
                                                  variant="outline-warning"
                                                  size="sm"
                                                  onClick={() => handleGeneratePurchaseRequest(item)}
                                                >
                                                  <BiCartAdd />
                                                </Button>
                                              </OverlayTrigger>
                                            )}
                                          </ButtonGroup>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </Accordion>
                );
              })()}
            </>
          )}

          {activeTab === 'purchase' && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Select
                    value={poStatusFilter}
                    onChange={(e) => setPoStatusFilter(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="receiving">Receiving</option>
                    <option value="received">Received</option>
                  </Form.Select>
                </Col>
                <Col md={6} className="text-end">
                  <Link to="/purchase-orders" className="btn btn-outline-primary">
                    View All Purchase Orders <FiArrowRight className="ms-1" />
                  </Link>
                </Col>
              </Row>

              <Card>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Order #</th>
                          <th>Vendor</th>
                          <th>Status</th>
                          <th>Payment</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPurchaseOrders.slice(0, 15).map((po) => (
                          <tr key={po._id}>
                            <td>
                              <strong className="text-primary">{po.orderId}</strong>
                              <br />
                              <small className="text-muted">
                                {new Date(po.orderDate).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <div>{po.vendor?.business_name || 'N/A'}</div>
                              {/* <small className="text-muted">
                                Value: ₹{po.finalPrice?.toLocaleString() || 0}
                              </small> */}
                            </td>
                            <td>
                              {getPOStatusBadge(po.status)}
                            </td>
                            <td>
                              <Badge bg="secondary" className="small">
                                {po.paymentStatus || 'Pending'}
                              </Badge>
                            </td>
                            <td>
                              <Link
                                to={`/purchase-orders/${po._id}`}
                                className="btn btn-outline-primary btn-sm"
                              >
                                <FiEye />
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}

          {activeTab === 'pmstock' && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder="Search packaging materials..."
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                  />
                </Col>
                <Col md={6}>
                  <Form.Select
                    value={stockFilter}
                    onChange={(e) => setStockFilter(e.target.value)}
                  >
                    <option value="all">All Stock</option>
                    <option value="low">Low Stock Only</option>
                    <option value="available">Available Stock</option>
                  </Form.Select>
                </Col>
              </Row>

              <Card>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Packing Material</th>
                          <th>Available Stock</th>
                          <th>Unit</th>
                          <th>Reorder Level</th>
                          <th>Last Restocked</th>
                          <th>Last Used</th>
                          <th>Status</th>
                          {/* <th>Warehouses</th> */}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filteredPmStock = pmStockSummary.filter(item => {
                            const matchesProduct = productFilter === '' ||
                              item.packingMaterial.name.toLowerCase().includes(productFilter.toLowerCase());

                            let matchesStatus = true;
                            if (stockFilter === 'low') {
                              const availableStock = item.totalAvailableStock || 0;
                              matchesStatus = availableStock <= (item.packingMaterial.reorderQuantity || 0);
                            } else if (stockFilter === 'available') {
                              const availableStock = item.totalAvailableStock || 0;
                              matchesStatus = availableStock > (item.packingMaterial.reorderQuantity || 0);
                            }

                            return matchesProduct && matchesStatus;
                          });

                          return filteredPmStock.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                <div className="text-muted">No packaging material stock found</div>
                              </td>
                            </tr>
                          ) : (
                            filteredPmStock.map(item => (
                              <tr key={item.packingMaterial._id}>
                                <td>
                                  <div>
                                    <strong className="text-primary">
                                      {item.packingMaterial.name}
                                    </strong>
                                    <br />
                                    <small className="text-muted">
                                      {item.packingMaterial.alias} - {item.packingMaterial.size}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  <strong className={`fw-semibold ${(item.totalAvailableStock || 0) <= 0 ? 'text-danger' : (item.totalAvailableStock || 0) <= (item.packingMaterial.reorderQuantity || 0) ? 'text-warning' : 'text-success'}`}>
                                    {item.totalAvailableStock || 0}
                                  </strong>
                                  {/* <div className="mt-1">
                                    {item.stockByLocation?.map((location, index) => (
                                      <small key={index} className="text-muted d-block">
                                        {location.warehouseLocation?.name || 'Unknown'}: {location.availableQuantity} {location.unit || item.packingMaterial.quantityUnit}
                                      </small>
                                    ))}
                                  </div> */}
                                </td>
                                <td>{item.packingMaterial.quantityUnit || 'pcs'}</td>
                                <td>{item.packingMaterial.reorderQuantity || 0}</td>
                                <td>
                                  {item.stockByLocation?.find(loc => loc.lastInwardAt)?.lastInwardAt ?
                                    new Date(item.stockByLocation.find(loc => loc.lastInwardAt).lastInwardAt).toLocaleDateString() :
                                    'N/A'
                                  }
                                </td>
                                {/* <td>
                                  {item.stockByLocation?.find(loc => loc.lastOutwardAt)?.lastOutwardAt ?
                                    new Date(item.stockByLocation.find(loc => loc.lastOutwardAt).lastOutwardAt).toLocaleDateString() :
                                    'N/A'
                                  }
                                </td> */}
                                <td>{getPMStockStatusBadge(item)}</td>
                                <td>
                                  <Badge bg="info">
                                    {item.stockByLocation?.length || 0} locations
                                  </Badge>
                                </td>
                                <td>
                                  <ButtonGroup size="sm">
                                    <Link
                                      to={`/packaging-material-stock-details/${item.packingMaterial._id}`}
                                      className="btn btn-outline-primary btn-sm"
                                    >
                                      <FiEye className="me-1" />
                                      View
                                    </Link>

                                    {/* Purchase Order Button for Low/Zero Stock */}
                                    {(() => {
                                      const availableStock = item.totalAvailableStock || 0;
                                      const reorderLevel = item.packingMaterial.reorderQuantity || 0;
                                      const reorderLevelNum = reorderLevel && !isNaN(reorderLevel) ? parseInt(reorderLevel) : 0;
                                      const availableQuantityNum = availableStock && !isNaN(availableStock) ? parseInt(availableStock) : 0;
                                      const hasReorderLevel = reorderLevelNum > 0;
                                      const isLowStock = hasReorderLevel && availableQuantityNum <= reorderLevelNum;
                                      const isNearReorderLevel = hasReorderLevel && availableQuantityNum <= reorderLevelNum * 1.5;
                                      const showPurchaseButton = ((isLowStock || isNearReorderLevel) || ((!hasReorderLevel) && (availableQuantityNum <= 50)));

                                      return showPurchaseButton ? (
                                        <OverlayTrigger
                                          placement="top"
                                          overlay={
                                            <Tooltip>
                                              {isLowStock ? 'Reorder Required' :
                                                !hasReorderLevel && availableQuantityNum <= 10 ? 'Low Stock - Reorder' :
                                                  !hasReorderLevel && availableQuantityNum <= 50 ? 'Consider Reordering' :
                                                    'Consider Reordering'}
                                            </Tooltip>
                                          }
                                        >
                                          <Button
                                            variant={isLowStock || (!hasReorderLevel && availableQuantityNum <= 10) ? "danger" : "warning"}
                                            size="sm"
                                            onClick={() => {
                                              navigate('/new-packaging-material-po', {
                                                state: {
                                                  preSelectedMaterial: item.packingMaterial,
                                                  warehouseLocation: item.stockByLocation?.[0]?.warehouseLocation,
                                                  currentStock: item.totalAvailableStock,
                                                  reorderLevel: item.packingMaterial.reorderQuantity
                                                }
                                              });
                                            }}
                                          >
                                            <FiShoppingCart />
                                          </Button>
                                        </OverlayTrigger>
                                      ) : null;
                                    })()}
                                  </ButtonGroup>
                                </td>
                              </tr>
                            ))
                          );
                        })()}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </div>

        <DashboardModals
          selectedSO={selectedSO}
          showItemsModal={showItemsModal}
          setShowItemsModal={setShowItemsModal}
          selectedItems={selectedItems}
          showStockDetailModal={showStockDetailModal}
          setShowStockDetailModal={setShowStockDetailModal}
          selectedProduct={selectedProduct}
          showReservedStockModal={showReservedStockModal}
          setShowReservedStockModal={setShowReservedStockModal}
          selectedReservedProduct={selectedReservedProduct}
          loadingReservedStock={loadingReservedStock}
          reservedStockOrders={reservedStockOrders}
          showBOMModal={showBOMModal}
          setShowBOMModal={setShowBOMModal}
          selectedItem={selectedItem}
          loadingBOM={loadingBOM}
          bomList={bomList}
          stockSummary={stockSummary}
          handleCreatePurchaseRequest={handleCreatePurchaseRequest}
          handleCreateCommonPurchaseRequest={handleCreateCommonPurchaseRequest}
          handleCreateFGPR={handleCreateFGPR}
          showProductionBatchModal={showProductionBatchModal} setShowProductionBatchModal={setShowProductionBatchModal}
          loadingBatches={loadingBatches}
          productionBatches={productionBatches}
          handleAssignToBatch={handleAssignToBatch}
          handleRaiseProductionRequest={handleRaiseProductionRequest}
        />

        {/* Reserved Stock Modal */}
        <Modal
          show={showReservedStockModal}
          onHide={() => {
            setTimeout(() => {
              setShowReservedStockModal(false);
              setSelectedReservedProduct(null);
            }, 100);
          }}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Reserved Stock Details
              {selectedReservedProduct?.productName && (
                <> - {selectedReservedProduct.productName}</>
              )}
              {selectedReservedProduct?.product?.productName && (
                <> - {selectedReservedProduct.product.productName}</>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingReservedStock ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading reserved stock details...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Quantity</th>
                      <th>Status</th>
                      <th>Expected Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservedStockOrders.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="text-muted">No reserved stock found</div>
                        </td>
                      </tr>
                    ) : (
                      reservedStockOrders.map(order => (
                        <tr key={order.orderId}>
                          <td>
                            <strong>{order.orderNumber}</strong>
                            <br />
                            <small className="text-muted">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <Badge bg="warning">
                              {order.quantity} {order.unit}
                            </Badge>
                          </td>
                          <td>
                            <Badge bg="info">{order.status}</Badge>
                          </td>
                          <td>
                            {order.expectedDate ?
                              new Date(order.expectedDate).toLocaleDateString() :
                              'N/A'
                            }
                          </td>
                          <td>
                            <Link
                              to={`/sales-orders/${order.orderId}`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              View
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setTimeout(() => {
                  setShowReservedStockModal(false);
                  setSelectedReservedProduct(null);
                }, 100);
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Pending POs Modal */}
        <Modal
          show={showPendingPOModal}
          onHide={() => {
            setTimeout(() => {
              setShowPendingPOModal(false);
              setSelectedProduct(null);
              setSelectedProductPOs([]);
            }, 100);
          }}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Pending Purchase Orders
              {selectedProduct?.productName && (
                <> - {selectedProduct.productName}</>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingPendingPOs ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading pending purchase orders...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>PO Number</th>
                      <th>Vendor</th>
                      <th>Total Quantity</th>
                      <th>Received Quantity</th>
                      <th>Pending Quantity</th>
                      <th>Status</th>
                      <th>Expected Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProductPOs.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          <div className="text-muted">No pending purchase orders found</div>
                        </td>
                      </tr>
                    ) : (
                      selectedProductPOs.map(po => (
                        <tr key={po._id}>
                          <td>
                            <strong>{po.purchaseOrderNumber}</strong>
                            <br />
                            <small className="text-muted">
                              {new Date(po.createdAt).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <div>
                              <strong>{po.vendor?.companyName || 'N/A'}</strong>
                              <br />
                              <small className="text-muted">{po.vendor?.contactPersonName || ''}</small>
                            </div>
                          </td>
                          <td>
                            <strong className="text-primary">
                              {po.totalQuantity} {selectedProduct?.unitId?.unitCode || ''}
                            </strong>
                          </td>
                          <td>
                            <strong className="text-success">
                              {po.receivedQuantity || 0} {selectedProduct?.unitId?.unitCode || ''}
                            </strong>
                          </td>
                          <td>
                            <strong className="text-warning">
                              {po.pendingQuantity} {selectedProduct?.unitId?.unitCode || ''}
                            </strong>
                          </td>
                          <td>
                            <Badge bg={po.status === 'Created' ? 'secondary' : po.status === 'Partial Received' ? 'warning' : 'success'}>
                              {po.status}
                            </Badge>
                          </td>
                          <td>
                            {po.expectedDeliveryDate ?
                              new Date(po.expectedDeliveryDate).toLocaleDateString() :
                              'Not specified'
                            }
                          </td>
                          <td>
                            <Link
                              to={`/purchase-orders/${po._id}`}
                              className="btn btn-outline-primary btn-sm"
                            >
                              <FiEye className="me-1" />
                              View
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setTimeout(() => {
                  setShowPendingPOModal(false);
                  setSelectedProduct(null);
                  setSelectedProductPOs([]);
                }, 100);
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Running Production Batches Modal */}
        <Modal
          show={showRunningBatchesModal}
          onHide={() => {
            setTimeout(() => {
              setShowRunningBatchesModal(false);
              setSelectedProduct(null);
              setSelectedProductBatches([]);
            }, 100);
          }}
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Running Production Batches - Input Material
              {selectedProduct?.productName && (
                <> - {selectedProduct.productName}</>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {loadingRunningBatches ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading running production batches...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Batch Number</th>
                      <th>Input Quantity</th>
                      <th>Status</th>
                      <th>Start Date</th>
                      <th>Completion Date</th>
                      <th>Production Request</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedProductBatches.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <div className="text-muted">No running production batches found</div>
                        </td>
                      </tr>
                    ) : (
                      selectedProductBatches.map(batch => (
                        <tr key={batch._id}>
                          <td>
                            <strong>{batch.batchNumber}</strong>
                            <br />
                            <small className="text-muted">
                              {new Date(batch.createdAt).toLocaleDateString()}
                            </small>
                          </td>
                          <td>
                            <strong className="text-success">
                              {batch.quantity} {selectedProduct?.unitId?.unitCode || ''}
                            </strong>
                          </td>
                          <td>
                            <Badge bg={
                              batch.status === 'draft' ? 'secondary' :
                                batch.status === 'in-progress' ? 'warning' :
                                  batch.status === 'paused' ? 'danger' : 'info'
                            }>
                              {batch.status}
                            </Badge>
                          </td>
                          <td>
                            {batch.startedAt ?
                              new Date(batch.startedAt).toLocaleDateString() :
                              'Not started'
                            }
                          </td>
                          <td>
                            {batch.completedAt ?
                              new Date(batch.completedAt).toLocaleDateString() :
                              'In progress'
                            }
                          </td>
                          <td>
                            {batch.productionRequest?.requestNumber ? (
                              <Link
                                to={`/production-requests/${batch.productionRequest._id}`}
                                className="text-decoration-none"
                              >
                                {batch.productionRequest.requestNumber}
                              </Link>
                            ) : (
                              <small className="text-muted">No PR linked</small>
                            )}
                          </td>
                          <td>
                            <Link
                              to={`/production/batches/${batch._id}`}
                              className="btn btn-outline-primary btn-sm"
                            >
                              <FiEye className="me-1" />
                              View
                            </Link>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setTimeout(() => {
                  setShowRunningBatchesModal(false);
                  setSelectedProduct(null);
                  setSelectedProductBatches([]);
                }, 100);
              }}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Purchase Request Modal */}
        <PurchaseRequestModal
          show={showPurchaseRequestCreateModal}
          onHide={() => {
            // Add delay to prevent React DOM errors
            setTimeout(() => {
              setShowPurchaseRequestCreateModal(false);
              setSelectedProduct(null);
            }, 100);
          }}
          product={selectedProduct}
          onSuccess={() => {
            // Add delay and proper state management
            setTimeout(() => {
              setShowPurchaseRequestCreateModal(false);
              setSelectedProduct(null);
              fetchDashboardData(); // Refresh the dashboard data
            }, 500);
          }}
        />

        {/* Allocation Suggestion Modal */}
        {currentSuggestion && (
          <AllocationSuggestionModal
            show={showAllocationModal}
            onHide={() => {
              setShowAllocationModal(false);
              setCurrentSuggestion(null);
              loadPendingSuggestions();
            }}
            suggestion={currentSuggestion}
            onAllocate={async (allocations) => {
              try {
                await axios.post(
                  `${config.API_BASE_URL}/allocation/suggestions/${currentSuggestion._id}/allocate`,
                  { allocations }
                );
                toast.success('Stock allocated successfully');
                setShowAllocationModal(false);
                setCurrentSuggestion(null);
                await fetchDashboardData();
                await loadPendingSuggestions();
              } catch (error) {
                console.error('Allocation error:', error);
                toast.error(error.response?.data?.message || 'Failed to allocate stock');
              }
            }}
            onDismiss={async () => {
              try {
                await axios.post(
                  `${config.API_BASE_URL}/allocation/suggestions/${currentSuggestion._id}/dismiss`
                );
                setShowAllocationModal(false);
                setCurrentSuggestion(null);
                await loadPendingSuggestions();
              } catch (error) {
                console.error('Dismiss error:', error);
                toast.error('Failed to dismiss suggestion');
              }
            }}
          />
        )}

      </Container>
    </Layout>
  );
};

export default OperationsDashboard;
